import axios from "axios";
import moment from "moment";
import { API_URL, ACTIONS, SAGA_ACTIONS, APIS, KEYS, STORAGE, URLS } from "../../_config";
import toast from "react-hot-toast";

const axiosApiInstance = axios.create();

var retrying = false;
var sessionTimeoutToastShown = false;
var lastSessionTimeoutTime = 0;
const waitForRetry = () => {
    var timer: any = null;
    return new Promise((resolve) => {
        timer = setInterval(() => {
            if (!retrying) {
                clearInterval(timer);
                resolve(true);
            }
        }, 1000);
    });
};

axiosApiInstance.interceptors.response.use(
    (response: any) => {
        return response;
    },
    async function (error: any) {
        const originalRequest = error.config;

        // On login page, don't try to refresh token - just reject the error
        // so the login component can handle it and show the error message
        if (window.location.pathname === URLS.AUTH.LOGIN) {
            return Promise.reject(error);
        }
        
        if (
            (error.response?.status == 401 ||
                ((window as any).location.protocol == "http" && error.code == "ERR_NETWORK")) &&
            !originalRequest?._retry
        ) {
            if (retrying) {
                await waitForRetry();
                originalRequest._retry = true;
                originalRequest.headers["Authorization"] = "Bearer " + localStorage.getItem(KEYS.AUTH_ACCESS);
                return axiosApiInstance(originalRequest);
            } else {
                retrying = true;
                originalRequest._retry = true;
                const access_token = await refreshAccessToken();
                if (!access_token) return Promise.reject(error); // Bug 1 fix: don't retry with null token
                originalRequest.headers["Authorization"] = "Bearer " + access_token;
                return axiosApiInstance(originalRequest);
            }
        } else {
            console.log(error.status);
            // console.log(error);
            if (error.status == 400 || error.status == 500 || error.status == 404) {
                return Promise.reject(error);
            }

            clearSessionAndRedirect(); // Bug 3 fix: use clearSessionAndRedirect so REFRESH_TOKEN_EXPIRY is also cleared
        }
        return Promise.reject(error);
    }
);

const isRefreshTokenExpired = () => {
    const expiry = localStorage.getItem(KEYS.REFRESH_TOKEN_EXPIRY);
    if (!expiry) return false;
    return moment().isAfter(moment(expiry));
};

const clearSessionAndRedirect = () => {
    sessionTimeoutToastShown = false;
    lastSessionTimeoutTime = 0;
    localStorage.removeItem("persist:root");
    localStorage.removeItem(STORAGE);
    localStorage.removeItem(KEYS.AUTH_ACCESS);
    localStorage.removeItem(KEYS.AUTH_REFRESH);
    localStorage.removeItem(KEYS.REFRESH_TOKEN_EXPIRY);
    (window as any).location.href = URLS.AUTH.LOGIN;
};

const refreshAccessToken = async () => {
    if (isRefreshTokenExpired()) {
        retrying = false; // Bug 2 fix: reset flag before early exit
        toast.error("Session expired. Please log in again.");
        clearSessionAndRedirect();
        return null;
    }

    let token: string | null = null;
    const payload = { refreshToken: getRefreshToken() };

    await axios
        .post(APIS.AUTH.REFRESH_TOKEN, payload)
        .then((response) => {
            const data = response.data?.data;
            const newToken = data?.token;
            const newRefreshToken = data?.refreshToken;
            const newExpiry = data?.refreshTokenExpiryUtc
                ? moment.utc(data.refreshTokenExpiryUtc).local().toISOString()
                : null;

            saveToken(newToken, newRefreshToken, newExpiry);
            token = newToken;
            sessionTimeoutToastShown = false;
            lastSessionTimeoutTime = 0;
            retrying = false;

            // Sync new tokens into Redux store, then re-fetch admin menus (mirrors login flow)
            import("../index").then(({ store }) => {
                store.dispatch({
                    type: ACTIONS.USER.UPDATE_TOKENS,
                    payload: { accessToken: newToken, refreshToken: newRefreshToken, refreshTokenExpiryTime: newExpiry },
                });
                store.dispatch({ type: SAGA_ACTIONS.USER.GET_MENUS, payload: {} });
            });
        })
        .catch(() => {
            retrying = false;
            const now = Date.now();
            if (!sessionTimeoutToastShown || now - lastSessionTimeoutTime > 30000) {
                sessionTimeoutToastShown = true;
                lastSessionTimeoutTime = now;
                toast.error("Session timed out");
            }
            clearSessionAndRedirect();
        });

    return token;
};

function saveToken(accessToken: string, refreshToken: string, refreshTokenExpiry: string | null = null) {
    localStorage.setItem(KEYS.AUTH_ACCESS, accessToken);
    localStorage.setItem(KEYS.AUTH_REFRESH, refreshToken);
    if (refreshTokenExpiry) {
        localStorage.setItem(KEYS.REFRESH_TOKEN_EXPIRY, refreshTokenExpiry);
    }

    const token_str = localStorage.getItem(STORAGE);
    if (token_str) {
        const data = JSON.parse(token_str);
        data.token = accessToken;
        data.refreshToken = refreshToken;
        localStorage.setItem(STORAGE, JSON.stringify(data));
    }
}

function getToken() {
    return localStorage.getItem(KEYS.AUTH_ACCESS);
}

function getRefreshToken() {
    return localStorage.getItem(KEYS.AUTH_REFRESH) || null;
}

const apiCatch = (error: any) => {
    if (error.response && error.response.status === 401 && window.location.pathname != URLS.AUTH.LOGIN) {
        //localStorage.removeItem(STORAGE)
        //window.location.href = process.env.PUBLIC_URL + URLS.AUTH.LOGIN;
    }
};

export const responseHandler = (resp: any) => {
    if (resp.status >= 200 && resp.status < 300 && resp.data) {
        if (resp.data.data.exceptions?.length) {
            return { status: false, data: resp.data.data.exceptions.join(",") };
        } else {
            return { status: true, data: resp.data };
        }
    } else {
        return { status: false, data: resp.data };
    }
};

export class CallApi {
    static GET(url: any, params: any, isAuth: boolean = false, isAdaptive: boolean = false) {
        return new Promise(async (resolve, reject) => {
            let urls = new URL(window?.location?.href);
            let headers: any = {
                "Content-Type": "application/json",
                AppHost: urls.hostname,
            };
            if (isAuth || isAdaptive) {
                let token = getToken();
                if (token) {
                    headers.Authorization = `Bearer ${token}`;
                }
            }

            let config: any = { headers, params };

            if (isAdaptive) {
                config = { headers, params, responseType: "arraybuffer" };
            }

            return axiosApiInstance
                .get(url, { ...config })
                .then((success: any) => {
                    return resolve(success);
                })
                .catch((error: any) => {
                    apiCatch(error);
                    return reject(error.response);
                });
        });
    }

    static POST(
        url: any,
        params: any,
        isAuth: boolean = false,
        isAdaptive: boolean = false,
        onUploadProgress: any = (e: any) => { }
    ) {
        return new Promise((resolve, reject) => {
            const urls = new URL(window?.location?.href);
            let headers: any = {};
            const token = getToken();
            if (isAuth || token) {
                const token = getToken();
                if (token) {
                    headers.Authorization = `Bearer ${token}`;
                    headers.AppHost = urls.hostname;
                }
            } else {
                headers.AppHost = urls.hostname;
            }
            let config: any = { headers, onUploadProgress };

            if (isAdaptive) {
                config = { headers, onUploadProgress, responseType: "arraybuffer" };
            }
            return axiosApiInstance
                .post(url, params, config)
                .then((success: any) => {
                    return resolve(success);
                })
                .catch((error: any) => {
                    apiCatch(error);
                    return reject(error.response);
                });
        });
    }

    static PUT(url: string, params: any, isAuth: boolean = false, isAdaptive: any = (e: any) => { }) {
        return new Promise((resolve, reject) => {
            const urls = new URL(window?.location?.href);
            let headers: any = { "Content-Type": "application/json" };
            if (isAuth || isAdaptive) {
                const token = getToken();
                if (token) {
                    headers.Authorization = `Bearer ${token}`;
                    headers.AppHost = urls.hostname;
                }
            } else {
                headers.AppHost = urls.hostname;
            }
            return axiosApiInstance
                .put(url, params, { headers: headers, onUploadProgress: isAdaptive })
                .then((success: any) => {
                    return resolve(success);
                })
                .catch((error: any) => {
                    apiCatch(error);
                    return reject(error.response);
                });
        });
    }

    static DELETE(url: string, params: any, isAuth: boolean = false, isAdaptive: any = (e: any) => { }) {
        return new Promise((resolve, reject) => {
            const urls = new URL(window?.location?.href);
            let headers: any = { "Content-Type": "application/json" };
            const token = getToken();
            if (isAuth || token) {
                if (token) {
                    headers.Authorization = `Bearer ${token}`;
                    headers.AppHost = urls.hostname;
                }
            } else {
                headers.AppHost = urls.hostname;
            }

            return axiosApiInstance
                .delete(url, { headers: headers, onUploadProgress: isAdaptive, data: params })
                .then((success: any) => {
                    return resolve(success);
                })
                .catch((error: any) => {
                    apiCatch(error);
                    return reject(error.response);
                });
            /* return axiosApiInstance({ method: 'DELETE', url, headers, params, }).then(success => {
                return resolve(success)
            }).catch(error => {
                apiCatch(error)
                return reject(error.response)
            }) */
        });
    }
}
